import { useCallback, useState } from "react";
import { client } from "../../Clients";
import {
	normalizeOutOfSyncItem,
	normalizeAnalysis,
	type PageOutOfSyncItem,
	type DeploymentPageAnalysis
} from "./deploymentUtils";

/** Out-of-sync list shown before analysis. */
export const usePagesOutOfSync = (target: "preview" | "production") => {
	const [items, setItems] = useState<PageOutOfSyncItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async (cacheBust?: number) => {
		setLoading(true);
		setError(null);
		try {
			const res = await client.deployment.apiDeploymentPageOutOfSyncGet(target, {
				withCredentials: true,
				headers: { Accept: "application/json" },
				...(cacheBust != null && { params: { _: cacheBust } })
			});
			const data = Array.isArray(res.data) ? res.data : [];
			setItems(data.map(normalizeOutOfSyncItem));
		} catch (err) {
			console.error(err);
			setItems([]);
			setError("Unable to load out-of-sync pages.");
		} finally {
			setLoading(false);
		}
	}, [target]);

	return { items, loading, error, load };
};

/** Analyze uses server-side out-of-sync list and analysis results. */
export const useDeploymentAnalysis = (target: "preview" | "production") => {
	const [pageAnalyses, setPageAnalyses] = useState<DeploymentPageAnalysis[]>([]);
	const [analyzing, setAnalyzing] = useState(false);
	const [analyzed, setAnalyzed] = useState(false);

	const analyze = useCallback(async (cacheBust?: number) => {
		setAnalyzing(true);
		try {
			const outOfSyncRes = await client.deployment.apiDeploymentPageOutOfSyncAnalysisGet(target, true, {
				withCredentials: true,
				headers: { Accept: "application/json" },
				...(cacheBust != null && { params: { _: cacheBust } })
			});
			const analyses = Array.isArray(outOfSyncRes.data)
				? outOfSyncRes.data.map(normalizeAnalysis)
				: [];
			if (!Array.isArray(analyses) || analyses.length === 0) {
				setPageAnalyses([]);
				setAnalyzed(true);
				return;
			}
			const results = analyses
				.filter((analysis) => analysis.slug.trim() !== "")
				.sort((a, b) => a.slug.localeCompare(b.slug) || a.language.localeCompare(b.language));
			setPageAnalyses(results);
			setAnalyzed(true);
		} catch (err) {
			console.error(err);
			setPageAnalyses([]);
		} finally {
			setAnalyzing(false);
		}
	}, [target]);
	return { pageAnalyses, analyzing, analyzed, analyze };
};
